import { create } from 'zustand'
import { IconName } from '@ui/elements/Icon'
import nanoid from '@ui/utils/nanoid'

import Toast from './Toast'
import { ToastVariant } from './types'

const initialState = {
  toasts: new Map(),
}

type ToastConfig = {
  duration?: number
  id?: string
}

export type ToastNodeItem = {
  id: string
  cratedAt: number
  node: React.ReactNode
  duration?: number
  variant: ToastVariant
}

export type ToastOptions = {
  node?: React.ReactNode
  text?: string
  title?: string
  icon?: IconName
  variant?: ToastVariant
} & ToastConfig

type ToastManagerState = {
  toasts: Map<string, ToastNodeItem>
  createToast: (options: ToastOptions) => void
  removeToast: (id: string) => void
}

const getToastNode = (item: ToastOptions) => {
  if (item.node) {
    return item.node
  }
  return (
    <Toast
      title={item.title}
      text={item.text}
      icon={item.icon}
      variant={item.variant}
    />
  )
}

const durations: Record<string, number> = {
  error: 10000,
  default: 5000,
} as const

export const useToastManager = create<ToastManagerState>((set) => ({
  ...initialState,

  createToast: (options: ToastOptions) => {
    set((state) => {
      const toasts = new Map<string, ToastNodeItem>(state.toasts)
      const id = options?.id || nanoid()
      const variant = options?.variant || 'info'
      toasts.set(id, {
        id,
        cratedAt: Date.now(),
        node: getToastNode(options),
        duration: durations[variant] || durations.default,
        variant,
        ...options,
      } satisfies ToastNodeItem)
      return { toasts }
    })
  },
  removeToast: (id: string) => {
    set((state) => {
      const toasts = new Map<string, ToastNodeItem>(state.toasts)
      toasts.delete(id)
      return { toasts }
    })
  },
}))

export const createToast = (options: ToastOptions) => {
  const createToast = useToastManager.getState().createToast
  return createToast(options)
}

export const useToast = () => useToastManager((s) => s.createToast)
